import { useMutation } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router';
import { useAuthStore } from '@/state/auth.state';
import {
    IconChartDots,
    IconChartRadar,
    IconCloudDataConnection,
    IconGift,
    IconLinkPlus,
    IconMail,
    IconUsersGroup,
    IconLogout,
    IconHelpSquareRoundedFilled,
    IconCoinFilled,
    IconUserFilled, IconAd,
} from '@tabler/icons-react';
import { NavLink, Transition } from '@mantine/core';
import type { MouseEventHandler, ReactNode } from 'react';
import { useUserState } from '@/state/user.state';
import { AuthCheckDtoRole, authControllerLogout } from '@/api-client/api';
import { queryClient } from '@/api-client/query-client';


export function AppNavigation({ onNavLinkClick, visibleTitles = true }: {
    onNavLinkClick(): void;
    visibleTitles?: boolean;
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useUserState();
    
    const authState = useAuthStore();
    
    const { mutateAsync: fetchLogout } = useMutation({
        mutationFn: async () => {
            await authControllerLogout();
        },
    });
    
    const pathParts = location.pathname.split('/').filter((i) => i);
    
    const items: {
        label: ReactNode;
        to: string;
        target?: string;
        icon: ReactNode;
        onClick?: MouseEventHandler;
        roles: AuthCheckDtoRole[],
    }[] = [
        {
            label: 'Статистика',
            to: '/ref-statistic',
            icon: <IconChartDots size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin ],
        },
        {
            label: 'Пользователи',
            to: '/users',
            icon: <IconUserFilled size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin, AuthCheckDtoRole.support ],
        },
        {
            label: 'Ref ссылки',
            to: '/ref-links',
            icon: <IconLinkPlus size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin ],
        },
        {
            label: 'Платежи',
            to: '/payments',
            icon: <IconCoinFilled size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin, AuthCheckDtoRole.support ],
        },
        {
            label: 'Подписчики',
            to: '/subscribers',
            icon: <IconUsersGroup size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin ],
        },
        {
            label: 'Баннеры',
            to: '/banners',
            icon: <IconAd size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin ],
        },
        {
            label: 'Рассылки',
            to: '/mailings',
            icon: <IconMail size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin ],
        },
        {
            label: 'Промокоды',
            to: '/promo-codes',
            icon: <IconGift size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin ],
        },
        {
            label: 'Поддержка',
            to: '/support',
            icon: <IconHelpSquareRoundedFilled size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin, AuthCheckDtoRole.support ],
        },
        {
            label: 'Remna flow',
            to: '/remna',
            icon: <IconChartRadar size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin ],
        },
        {
            label: 'Remna',
            to: 'https://remna-st512.vpn-share.online/dashboard/management/users',
            target: '_blank',
            icon: <IconCloudDataConnection size={ 16 } stroke={ 1.5 }/>,
            roles: [ AuthCheckDtoRole.admin ],
        },
        {
            icon: <IconLogout size={ 16 } stroke={ 1.5 }/>,
            label: 'Выйти',
            to: '/logout',
            roles: [ AuthCheckDtoRole.admin, AuthCheckDtoRole.support ],
            onClick: async (e) => {
                e.preventDefault();
                await fetchLogout();
                window.localStorage.removeItem('session');
                authState.setIsAuthenticated(false);
                user.setRole(null);
                queryClient.clear();
                navigate('/sign-in');
            },
        },
    ];
    
    return (
        <>
            {
                items
                    .filter((item) => user.role && item.roles.includes(user.role))
                    .map((item, index) => (
                        <NavLink
                            key={ index }
                            component={ Link }
                            to={ item.to }
                            target={ item.target }
                            leftSection={ item.icon }
                            label={ (
                                <Transition
                                    mounted={ visibleTitles }
                                    transition='scale-x'
                                    duration={ 300 }
                                    timingFunction='ease'
                                >
                                    { (styles) => <div style={ styles }>{ item.label }</div> }
                                </Transition>
                            ) }
                            onClick={ (e) => {
                                onNavLinkClick();
                                item.onClick?.(e);
                            } }
                            active={ item.to === '/' + pathParts[0] }
                        />
                    ))
            }
        </>
    );
}